import { createSelector } from '@reduxjs/toolkit';

import { CancellationStatus, CancellationType, ScheduleCancellationViewModel } from '../../API/ClientAPIHelpers/scheduleCancellation';

interface RootState {
	scheduleCancellation: { providerCancellations: ScheduleCancellationViewModel[] };
}

export interface CancellationGroup {
	decisionNotes?: string;
	endDate: string;
	key: string;
	reasonName: string;
	startDate: string;
}

const selectProviderCancellations = (state: RootState) => state.scheduleCancellation.providerCancellations;

const groupByStatuses = (
	cancellations: ScheduleCancellationViewModel[],
	statuses: CancellationStatus[],
): CancellationGroup[] => {
	const groups = new Map<string, CancellationGroup>();

	cancellations.forEach((d) => {
		if (
			d.type !== CancellationType.ExtendedTimeOff ||
			!statuses.includes(d.status)
		)
			return;

		const key = d.groupId ?? d.id;
		if (!groups.has(key)) {
			groups.set(key, {
				key,
				reasonName: d.reasonName || 'Cancellation',
				startDate: d.startDate,
				endDate: d.endDate,
				decisionNotes: d.decisionNotes ?? undefined,
			});
		}
	});

	return Array.from(groups.values());
};

export const selectMyPendingGroups = createSelector(
	[selectProviderCancellations],
	(cancellations) => groupByStatuses(cancellations, [CancellationStatus.Pending]),
);

export const selectMyApprovedGroups = createSelector(
	[selectProviderCancellations],
	(cancellations) => groupByStatuses(cancellations, [CancellationStatus.Approved, CancellationStatus.Applied]),
);

export const selectMyDeniedGroups = createSelector(
	[selectProviderCancellations],
	(cancellations) => groupByStatuses(cancellations, [CancellationStatus.Denied]),
);
